import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js'

import * as AWS from 'aws-sdk'

export const DEFAULT_PART_SIZE = 10 * 1024 * 1024
export const DEFAULT_QUEUE_SIZE = 4

const {REACT_APP_STAGE: stage, REACT_APP_AWS_DEFAULT_REGION: region} = process.env

const {
  [`REACT_APP_USER_POOL_ID_${stage}`]: userPoolId,
  [`REACT_APP_APP_CLIENT_ID_${stage}`]: clientId,
  [`REACT_APP_USER_IDENTITY_POOL_ID_${stage}`]: identityPoolId
} = process.env

AWS.config.region = region

// Initialize Lambda only after AWS Config credentials are set in the authentication processes
let lambda

// Reference Doc: https://github.com/aws-amplify/amplify-js/blob/main/packages/amazon-cognito-identity-js/README.md
const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId
})

const setCredentials = (idToken, resolve, reject, currentUser) => {
  const credentialParams = {
    IdentityPoolId: identityPoolId,
    Logins: {
      [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken
    }
  }

  let credentials = new AWS.CognitoIdentityCredentials(credentialParams)
  credentials.clearCachedId()

  credentials = new AWS.CognitoIdentityCredentials(credentialParams)

  AWS.config.region = region
  AWS.config.credentials = credentials
  AWS.config.credentials.refresh(error => {
    if (error) {
      reject(error)
    } else {
      resolve({
        authenticated: true,
        user: currentUser
      })
    }
  })

  lambda = new AWS.Lambda()
}

export const authUser = () => {
  return new Promise(async(resolve, reject) => {
    const currentUser = userPool.getCurrentUser()

    if (currentUser != null) {
      currentUser.getSession((err, session) => {
        if (err) {
          return reject(err)
        }

        if (session.isValid()) {
          setCredentials(session.getIdToken().getJwtToken(), resolve, reject, currentUser)
        }
        else {
          reject()
        }
      })
    }
    else {
      reject()
    }
  })
}

export const login = (username, password) => {
  const currentUser = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password
  })

  return new Promise((resolve, reject) =>
    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: result => {
        setCredentials(result.getIdToken().getJwtToken(), resolve, reject, currentUser)
      },
      onFailure: err => {
        if (err.code === 'PasswordResetRequiredException') {
          resolve({
            authenticated: false,
            passwordReset: true,
            user: currentUser
          })
        }

        reject(err)
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        resolve({
          authenticated: false,
          newPasswordRequired: true,
          user: currentUser
        })
      }
    })
  )
}

export const passwordReset = (user, newpassword) => {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newpassword, {email: user.username}, {
      onSuccess: result => resolve(),
      onFailure: err => {
        reject(err)
      }
    })
  })
}

export const passwordResetWithCode = (email, newpassword, code) => {
  const user = new CognitoUser({Username: email, Pool: userPool})

  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newpassword, {
      onSuccess: result => resolve(),
      onFailure: err => {
        reject(err)
      }
    })
  })
}

export const sendCode = email => {
  const user = new CognitoUser({Username: email, Pool: userPool})

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: result => resolve(),
      onFailure: err => {
        reject(err)
      }
    })
  })
}

// Change the signed-in user's password (needs a valid session; Cognito re-checks the current password)
export const changePassword = (user, oldPassword, newPassword) => {
  return new Promise((resolve, reject) => {
    user.changePassword(oldPassword, newPassword, (err, result) => {
      if (err) {
        return reject(err)
      }

      resolve(result)
    })
  })
}

export const getUserToken = user => {
  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        return reject(err)
      }

      resolve(session)
    })
  })
}

export const getUserAttributes = user => {
  return new Promise((resolve, reject) => {
    user.getUserAttributes((err, res) => {
      if (err) {
        return reject(err)
      }

      let attr = {}

      if (res) {
        res.map((a) => {
          return attr[a.getName()] = a.getValue()
        })

        resolve(attr)
      }
      else {
        resolve({})
      }
    })
  })
}

export const signOutUser = () => {
  const currentUser = userPool.getCurrentUser()

  if (currentUser !== null) {
    currentUser.signOut()
  }
}

export const getSession = user => {
  return new Promise((resolve, reject) => {
    user.getSession((err, res) => {
      if (err) {
        return reject(err)
      }

      resolve(res)
    })
  })
}

export const callLambda = (functionName, functionAlias, body) => {
  return new Promise((resolve, reject) => {
    if (!lambda) {
      const e = new Error('Your session has expired. Please sign in again.')
      e.code = 'SESSION_EXPIRED'
      return reject(e)
    }

    lambda.invoke({
      FunctionName: functionName,
      Qualifier: functionAlias,
      Payload: JSON.stringify(body)
    }, (err, data) => {
      if (err) {
        console.error('[ERROR]: ' + err)
        return reject(new Error(err))
      }
      else if (data['FunctionError']) {
        const payload = JSON.parse(data.Payload)
        console.error('[PAYLOAD]: ' + JSON.stringify(payload))

        return reject(new Error(payload.errorMessage))
      }
      else {
        let body = JSON.parse(data.Payload).body
        const dataToReturn = JSON.parse(body)

        return resolve(dataToReturn)
      }
    })
  })
}

// Station Portal contract: the Cognito-authenticated Lambda (Station-Portal-Get-Files) receives the
// accountId and contactId read from the user's Cognito attributes plus the Apex resource path.
// Every stationportal/* resource (getfiles, getfile, downloadfile, longterm*) uses this shape.
const callStationApex = async (user, resourceEnvKey, extraBody = {}, friendlyError) => {
  const {
    [`REACT_APP_FUNCTION_NAME_${stage}`]: functionName,
    [`REACT_APP_FUNCTION_ALIAS_${stage}`]: functionAlias,
    [resourceEnvKey]: resourcePath
  } = process.env

  const {
    'custom:PrimaryAccountSfid': accountId,
    'custom:ContactSfid': contactId
  } = await getUserAttributes(user)

  if (!accountId || accountId === '') {
    throw new Error('Account Id is missing')
  }

  if (!contactId || contactId === '') {
    throw new Error('Contact Id is missing')
  }

  try {
    return await callLambda(functionName, functionAlias, {
      body: {
        accountId,
        contactId,
        resourcePath,
        ...extraBody
      }
    })
  }
  catch (e) {
    console.error('[ERROR]: ' + e)
    throw new Error(friendlyError || e.message)
  }
}

export const getFiles = async user => {
  const data = await callStationApex(user, 'REACT_APP_APEX_REST_GET_FILES', {}, 'There was an error fetching files')

  data.files.forEach(e => {
    e.id = e.fileId
    e.deliveryDateTime = new Date(e.deliveryDateTime)
  })

  return data
}

export const getFile = async (user, fileId) => {
  const data = await callStationApex(user, 'REACT_APP_APEX_REST_GET_FILE', {fileId}, 'There was an error fetching the file')

  if (!data.files || data.files.length < 1) {
    throw new Error('There was an error fetching the file')
  }

  return data
}

export const downloadFile = (user, fileId) =>
  callStationApex(user, 'REACT_APP_APEX_REST_DOWNLOAD_FILE', {fileId}, 'There was an error fetching the link to the deliverable')

// Long Term Storage
// Login-time check: does this account get the Uploads tab? (Account Services Provided)
export const getLongTermAccess = user =>
  callStationApex(user, 'REACT_APP_APEX_REST_LONG_TERM_ACCESS', {}, 'There was an error checking upload access')

// Uploads tab data: upload target, Versions for the picklist, recent uploads, long-term files
export const getHubUploads = user =>
  callStationApex(user, 'REACT_APP_APEX_REST_HUB_UPLOADS', {})

// Signed source | proxy link for one file shown on the Uploads tab
export const getHubFileLink = (user, fileId, linkType) =>
  callStationApex(user, 'REACT_APP_APEX_REST_HUB_FILE_LINK', {fileId, linkType})

// Hard delete of one long-term file (and its proxy); Apex only allows the contact who uploaded it
export const deleteHubFile = (user, fileId) =>
  callStationApex(user, 'REACT_APP_APEX_REST_HUB_FILE_DELETE', {fileId})

export const registerUpload = upload => {
  let metadata = {}

  upload.metadata.forEach(m => {
    metadata[m.name] = m.value
  })

  const params = {
    Bucket: upload.bucket,
    Key: upload.key,
    Body: upload.payload,
    Metadata: metadata
  }

  const tags = upload.tags.map(tag => ({'Key': tag.name, 'Value': tag.value}))

  return new AWS.S3.ManagedUpload({
    partSize: DEFAULT_PART_SIZE,
    queueSize: DEFAULT_QUEUE_SIZE,
    params,
    tags,
    computeChecksums: true
  })
}
